import { useNavigate, useSearch } from "@tanstack/react-router";
import { Suspense } from "react";
import { authClient } from "@/lib/auth";
import LoadingSpinner from "../loading-spinner";
import ScrollContainer from "../ScrollContainer";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "../ui/dialog";
import { Separator } from "../ui/separator";
import { SidebarProvider } from "../ui/sidebar";
import ApiKeysSettings from "./ApiKeysSettings";
import AttributesSettings from "./AttributesSettings";
import BillingQuestions from "./BillingQuestions";
import DangerSettings from "./DangerSettings";
import NotificationSettings from "./NotificationSettings";
import PreferencesSettings from "./PreferencesSettings";
import ProfileSettings from "./ProfileSettings";
import SettingsSidebar from "./SettingsSidebar";
import SubscriptionSettings from "./SubscriptionSettings";
import TaskStatusSettings from "./TaskStatusSettings";
import TeamSettings from "./TeamSettings";
import UsersSettings from "./UsersSettings";

const settingsCategories = [
    { name: "Profile", slug: "profile", description: "Manage your own profile", roles: ["admin", "member", "owner"] },
    {
        name: "Preferences",
        slug: "preferences",
        description: "Choose how dates, times and weeks are shown to you",
        roles: ["admin", "member", "owner"],
    },
    // { name: "Team", slug: "team", description: "Manage general team settings", roles: ["owner"] },
    {
        name: "Notifications",
        slug: "notifications",
        description: "Manage your notification settings",
        roles: ["admin", "member", "owner"],
    },
    { name: "Team Users", slug: "users", description: "Manage the users of your team", roles: ["admin", "owner"] },
    {
        name: "Attributes",
        slug: "attributes",
        description: "Manage the attributes of your team",
        roles: ["admin", "owner"],
    },
    {
        name: "Task Statuses",
        slug: "task-statuses",
        description: "Manage the task statuses of your team",
        roles: ["admin", "owner"],
    },
    {
        name: "API Keys",
        slug: "api-keys",
        description: "Create and manage personal API keys for the Orsane API",
        roles: ["admin", "member", "owner"],
    },
    { name: "Billing", slug: "subscription", description: <BillingQuestions />, roles: ["owner"] },
    { name: "Danger Zone", slug: "danger", description: "Delete your team and all its data", roles: ["owner"] },
];

function SettingsTitle({ title, description }: { title: string; description: string | React.ReactNode }) {
    return (
        <div className="px-6 py-4">
            <DialogTitle className="text-xl font-medium pb-1">{title}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
        </div>
    );
}

export default function SettingsDialog() {
    const activeMember = authClient.useActiveMember();
    const settings = useSearch({ strict: false, select: (s: any) => s.settings }) as string | undefined;
    const settingsCategory = settingsCategories.find((category) => category.slug === settings)!;
    const navigate = useNavigate();
    const isOpen = settings !== undefined;

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            navigate({
                to: ".",
                search: (prev) => ({
                    ...prev,
                    settings: undefined,
                }),
            });
        }
    };

    const activeRole = activeMember.data?.role || "";
    const canDisplayCurrentSettings = settingsCategories.some(
        (category) => category.slug === settings && category.roles.includes(activeRole)
    );

    return (
        <Dialog modal={true} open={isOpen} onOpenChange={handleOpenChange}>
            <DialogContent className="p-0 sm:max-w-4xl sm:max-h-[700px] overflow-hidden bg-white">
                <div className="flex h-[calc(100vh-200px)] min-h-[450px] gap-0 p-0">
                    <div className="h-full">
                        <SidebarProvider className="h-full">
                            <div className="h-full overflow-y-auto">
                                <SettingsSidebar activeRole={activeRole} settingsCategories={settingsCategories} />
                            </div>
                        </SidebarProvider>
                    </div>
                    <div className="flex-1 overflow-hidden flex flex-col h-full bg-neutral-50/60">
                        {canDisplayCurrentSettings && (
                            <>
                                <SettingsTitle
                                    description={settingsCategory.description}
                                    title={settingsCategory.name}
                                />
                                <Separator />
                                <div className="flex-1 relative ">
                                    <Suspense
                                        fallback={
                                            <div className="p-6">
                                                <LoadingSpinner />
                                            </div>
                                        }
                                    >
                                        <ScrollContainer className="absolute inset-0 overflow-scroll">
                                            <div className="p-6">
                                                {settings === "profile" && <ProfileSettings />}
                                                {settings === "preferences" && <PreferencesSettings />}
                                                {settings === "team" && <TeamSettings />}
                                                {settings === "notifications" && <NotificationSettings />}
                                                {settings === "attributes" && <AttributesSettings />}
                                                {settings === "task-statuses" && <TaskStatusSettings />}
                                                {settings === "api-keys" && <ApiKeysSettings />}
                                                {settings === "subscription" && <SubscriptionSettings />}
                                                {settings === "users" && <UsersSettings />}
                                                {settings === "danger" && <DangerSettings />}
                                            </div>
                                        </ScrollContainer>
                                    </Suspense>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
